import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import {
  App,
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Row,
  Select,
  Space,
  Spin,
  Typography,
} from 'antd';
import { mutations, queries } from '../../../graphql/graphql';

const { Text } = Typography;

const formatDate = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const getTypeLabel = (type?: string) => {
  if (type === 'SITE_PLAN') return 'Site plan';
  if (type === 'PROPOSAL') return 'Proposal';
  return 'Order';
};

const extractSiteVisitId = (data?: Record<string, unknown>) => {
  if (!data) return undefined;
  const raw = data.siteVisitId;
  if (typeof raw === 'string') return raw;
  if (raw && typeof raw === 'object' && typeof (raw as { toString?: () => string }).toString === 'function') {
    const value = (raw as { toString: () => string }).toString();
    return value === '[object Object]' ? undefined : value;
  }
  return undefined;
};

const OrderEdit: React.FC = () => {
  const { message } = App.useApp();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const { data, loading, refetch } = useQuery(queries.order.ORDER, {
    variables: { id },
    skip: !id,
    fetchPolicy: 'network-only',
    onCompleted: result => {
      if (result?.order?.status) {
        form.setFieldsValue({ status: result.order.status });
      }
    },
  });

  const [updateOrder, { loading: updating }] = useMutation(mutations.order.ORDER_UPDATE, {
    onCompleted: () => {
      message.success('Order updated.');
      navigate(`/admin/order/${id}`);
    },
    onError: error => {
      message.error(error.message);
    },
  });

  useEffect(() => {
    refetch();
  }, [refetch]);

  const order = data?.order;
  const orderData = (order?.data || {}) as Record<string, unknown>;
  const siteVisitId = extractSiteVisitId(orderData);

  const onFinish = async (values: { status: string }) => {
    if (!id) return;
    await updateOrder({
      variables: {
        id,
        input: {
          status: values.status,
        },
      },
    });
  };

  if (loading && !order) {
    return (
      <Row justify="center" align="middle" style={{ minHeight: '60vh' }}>
        <Col>
          <Spin size="large" />
        </Col>
      </Row>
    );
  }

  if (!loading && !order) {
    return (
      <Row justify="center" align="middle" style={{ minHeight: '60vh' }}>
        <Col>
          <Card>
            <h2>Order not found</h2>
            <Button type="primary" onClick={() => navigate('/admin/orders')}>
              Back to Orders
            </Button>
          </Card>
        </Col>
      </Row>
    );
  }

  return (
    <Row gutter={[16, 16]} justify="center">
      <Col span={24}>
        <Card
          extra={
            <Space>
              <Button onClick={() => navigate(`/admin/order/${order.id}`)}>Back to view</Button>
              <Button onClick={() => navigate('/admin/orders')}>Back to list</Button>
            </Space>
          }>
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Order ID">{order.id}</Descriptions.Item>
            <Descriptions.Item label="Type">{getTypeLabel(order.type)}</Descriptions.Item>
            <Descriptions.Item label="Title">{order.title}</Descriptions.Item>
            <Descriptions.Item label="Created at">{formatDate(order.createdAt)}</Descriptions.Item>
            <Descriptions.Item label="Updated at">{formatDate(order.updatedAt)}</Descriptions.Item>
          </Descriptions>
        </Card>
      </Col>
      <Col span={24}>
        <Card title="Update status">
          <Form form={form} layout="vertical" onFinish={onFinish}>
            <Form.Item
              name="status"
              label="Status"
              rules={[{ required: true, message: 'Please select a status.' }]}>
              <Select
                options={[
                  { label: 'Waiting', value: 'WAITING' },
                  { label: 'Done', value: 'DONE' },
                ]}
              />
            </Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={updating}>
                Save
              </Button>
              {order.type === 'SITE_PLAN' && siteVisitId && (
                <Button onClick={() => navigate(`/site-visit/edit/${siteVisitId}`)}>
                  Edit site plan
                </Button>
              )}
            </Space>
            {order.type === 'SITE_PLAN' && !siteVisitId && (
              <div style={{ marginTop: 12 }}>
                <Text type="secondary">Site plan is not linked yet.</Text>
              </div>
            )}
          </Form>
        </Card>
      </Col>
    </Row>
  );
};

export default OrderEdit;
